import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Eye, Clock, Pencil, Trash2, RefreshCw, QrCode } from 'lucide-react';
import api from '../api/api';
import HourometerModal from '../components/HourometerModal';
import EquipmentModal from '../components/EquipmentModal';
import DeleteEquipmentModal from '../components/DeleteEquipmentModal';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = window.location.origin;

const STATUS_LABELS = { operative: 'Operativo', alert: 'Alerta', breakdown: 'Avería', inactive: 'Inactivo' };
const STATUS_CLASSES = {
  operative: 'badge-operative', alert: 'badge-alert',
  breakdown: 'badge-breakdown', inactive: 'badge-inactive'
};

export default function MaquinariaFija() {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [showHourometer, setShowHourometer] = useState(null);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [editEquipment, setEditEquipment] = useState(null);
  const [deleteEquipment, setDeleteEquipment] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get('/equipment?type=fixed')
      .then(r => setEquipment(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const sections = [...new Set(equipment.map(e => e.section).filter(Boolean))];

  const filtered = equipment.filter(e => {
    const matchSearch = !search || e.name.toLowerCase().includes(search.toLowerCase()) || e.code.toLowerCase().includes(search.toLowerCase());
    const matchSection = !sectionFilter || e.section === sectionFilter;
    return matchSearch && matchSection;
  });

  // Group by section
  const grouped = sections.reduce((acc, section) => {
    acc[section] = filtered.filter(e => e.section === section);
    return acc;
  }, {});
  const noSection = filtered.filter(e => !e.section);

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Maquinaria Fija</h1>
          <p className="text-sm text-gray-500 mt-0.5">{equipment.length} equipos · Planta de Tratamiento</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500">
            <RefreshCw size={16} />
          </button>
          <button
            onClick={() => setShowEquipmentModal(true)}
            className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            data-testid="add-fixed-equipment-btn"
          >
            <Plus size={16} /> Nuevo Equipo
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar equipo..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#16a34a]"
            data-testid="search-fixed"
          />
        </div>
        <select
          value={sectionFilter}
          onChange={e => setSectionFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#16a34a] bg-white"
        >
          <option value="">Todas las secciones</option>
          {sections.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]" />
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([section, items]) => (
            <div key={section}>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-bold text-gray-500 uppercase tracking-widest px-3 py-1 bg-gray-100 rounded-full">{section}</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Código</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Equipo</th>
                      <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Horas Totales</th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.map((eq) => (
                      <tr key={eq.id} className="table-row-hover" data-testid={`fixed-row-${eq.id}`}>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASSES[eq.status] || 'badge-inactive'}`}>
                            {STATUS_LABELS[eq.status] || eq.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-600">{eq.code}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">{eq.name}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-gray-900">
                          {(eq.total_hours || 0).toLocaleString('es-ES')} h
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => navigate(`/equipos/${eq.id}`)}
                              className="p-1.5 text-gray-400 hover:text-[#16a34a] hover:bg-green-50 rounded"
                              title="Ver detalle"
                              data-testid={`view-fixed-${eq.id}`}
                            >
                              <Eye size={15} />
                            </button>
                            <button
                              onClick={() => setShowHourometer(eq)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Registrar Horómetro"
                              data-testid={`hourometer-fixed-${eq.id}`}
                            >
                              <Clock size={15} />
                            </button>
                            <button
                              onClick={() => setEditEquipment(eq)}
                              className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                              title="Editar equipo"
                              data-testid={`edit-fixed-${eq.id}`}
                            >
                              <Pencil size={15} />
                            </button>
                            <a
                              href={`${API_URL}/api/equipment/${eq.id}/qr?base_url=${encodeURIComponent(APP_URL)}`}
                              target="_blank" rel="noreferrer"
                              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
                              title="Descargar QR"
                              data-testid={`qr-fixed-${eq.id}`}
                            >
                              <QrCode size={15} />
                            </a>
                            {user?.role === 'admin' && (
                              <button
                                onClick={() => setDeleteEquipment(eq)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Eliminar equipo"
                                data-testid={`delete-fixed-${eq.id}`}
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {noSection.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {noSection.map(eq => (
                <div key={eq.id} className="flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-0">
                  <span className="font-medium text-sm">{eq.name}</span>
                  <span className="font-mono text-sm">{eq.total_hours || 0} h</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showHourometer && (
        <HourometerModal equipment={showHourometer} onClose={() => setShowHourometer(null)} onSaved={load} />
      )}
      {showEquipmentModal && (
        <EquipmentModal type="fixed" onClose={() => setShowEquipmentModal(false)} onSaved={load} />
      )}
      {editEquipment && (
        <EquipmentModal type="fixed" equipment={editEquipment} onClose={() => setEditEquipment(null)} onSaved={load} />
      )}
      {deleteEquipment && (
        <DeleteEquipmentModal equipment={deleteEquipment} onClose={() => setDeleteEquipment(null)} onDeleted={load} />
      )}
    </div>
  );
}
